import { FC, useState } from 'react';
import { z } from 'zod';
import { Delete, Edit, Email, People } from '@mui/icons-material';
import { TableActions } from 'components/Table';
import { Box } from '@mui/material';
import trpcApi from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import Spacing from 'components/Spacing';
import { Typography } from '@mui/material';
import { AppTextInput } from 'components/FormElements';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  investorId: string;
  furtherUserId: string;
  firmId: string;
  investorHasSingleUserOnly: boolean;
  userOnlyAssociatedWithSameFirmInvestors: boolean;
  isInvestorsDefaultUser: boolean;
};

const InvestorAccessGrantActions: FC<Props> = ({
  investorId,
  furtherUserId,
  firmId,
  investorHasSingleUserOnly,
  userOnlyAssociatedWithSameFirmInvestors,
  isInvestorsDefaultUser,
}) => {
  const updateDisclosure = useDisclosure();
  const [newEmail, setNewEmail] = useState('');
  const [newEmailError, setNewEmailError] = useState('');
  const { isSuperAdmin } = useGetRole();
  const [canDelete, canEdit, canEditFurtherUser] = useHasPermissions([
    RolePermission.DeleteInvestorAccessGrant,
    RolePermission.EditInvestorAccessGrant,
    RolePermission.EditFurtherUser,
  ]);

  const updateDefaultEmail = trpcApi.investor.updateDefaultEmail.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Default email updated successfully.',
    }),
  );
  const remove = trpcApi.investorAccessGrant.remove.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'User removed successfully.',
    }),
  );
  const edit = trpcApi.furtherUser.update.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'User email edited successfully.',
      onSuccess: () => {
        updateDisclosure.onClose();
        setNewEmailError('');
      },
      onError: (error) => {
        if (error.data?.cause === 'This email is already in use.') {
          error.data.cause += ' Please add them as a new user instead.';
        }
      },
    }),
  );

  const { mutate: sendPasswordResetEmail, isPending: isSendingPasswordReset } =
    trpcApi.furtherUser.sendPasswordResetEmail.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Password reset email sent successfully.',
      }),
    );

  const allowEditingEmail =
    isSuperAdmin || userOnlyAssociatedWithSameFirmInvestors;

  return (
    <Box display="flex" justifyContent="centre" width="100%">
      <TableActions
        showAsDropdown
        actions={[
          {
            label: 'Remove user from investor',
            icon: Delete,
            visible: canDelete,
            color: 'error',
            disabled: investorHasSingleUserOnly,
            tooltip: investorHasSingleUserOnly
              ? 'Cannot delete this user, because the investor must have at least one user with access.'
              : undefined,
            onClick: () => remove.mutate({ investorId, furtherUserId, firmId }),
          },
          {
            label: "Edit user's email address",
            icon: Edit,
            color: 'primary',
            visible: canEditFurtherUser,
            disabled: !allowEditingEmail,
            tooltip: !allowEditingEmail
              ? "Cannot edit this user's email address, because they have access to investors, advisers or organisations in other firms."
              : undefined,
            onClick: () => updateDisclosure.onOpen(),
          },
          {
            label: 'Send password reset',
            icon: Email,
            color: 'primary',
            visible: canEditFurtherUser,
            disabled: isSendingPasswordReset,
            onClick: () => {
              sendPasswordResetEmail({
                furtherUserId,
                firmId,
              });
            },
          },
          {
            label: 'Make default email for this investor',
            icon: People,
            color: 'primary',
            visible:
              canEdit && !investorHasSingleUserOnly && !isInvestorsDefaultUser,
            tooltip:
              'Updating the investors default email will change the email address used as an identifier in the system, but not affect their ability to log in or receive emails.',
            onClick: () =>
              updateDefaultEmail.mutate({ furtherUserId, investorId }),
          },
        ]}
      />
      <AlertDialog
        size="sm"
        open={updateDisclosure.isOpen}
        onClose={() => {
          updateDisclosure.onClose();
          setNewEmailError('');
        }}
        onConfirm={() => {
          if (z.string().email().safeParse(newEmail).error) {
            setNewEmailError('Please enter a valid email address');
            return;
          }
          setNewEmailError('');
          edit.mutate({
            furtherUserId,
            email: newEmail,
          });
        }}
        title="Edit user email address"
        iconColor="primary"
        icon={Edit}
        content={
          <Spacing size="md">
            <Typography variant="body1">
              The user will need to log in with this new email address to access
              their account.{' '}
              {isSuperAdmin ? (
                <>
                  They will <strong>not</strong> be notified of this change.
                </>
              ) : (
                <>They will be notified of this change.</>
              )}
            </Typography>
            <Typography variant="body1">
              {isSuperAdmin
                ? 'The default emails of any linked investors, advisers or organisation users will also be updated. If the email is in use already, add them as a new user instead.'
                : 'The default emails of any linked investors will be updated. If the email is in use already, add them as a new user instead.'}
            </Typography>
            <AppTextInput
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
              }}
              helperText={newEmailError}
              label="New email address"
              name="email"
              fullWidth
            />
          </Spacing>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Update',
        }}
        confirmBtnProps={{ loading: edit.isPending }}
      />
    </Box>
  );
};

export default InvestorAccessGrantActions;
